<template>
	<div class="stamp-entry-page">
		<main class="stamp-entry-app">
			<section class="stamp-entry-hero">
				<img :src="heroImg" alt="2026 서울동물행복페스타 메인 비주얼" />
			</section>
			<section class="stamp-entry-content">
				<div class="stamp-entry-logo"><img :src="logoImg" alt="2026 서울동물행복페스타" /></div>
				<div class="stamp-entry-headline">
					<h1>스탬프 투어 참여하기</h1>
					<p>참여 확인을 위해 이름과 연락처를 입력해주세요.</p>
				</div>

				<form novalidate @submit.prevent="smsSent ? verifyAuthCode() : sendAuthCode()">
					<div class="stamp-entry-field">
						<label for="stampName">이름 입력</label>
						<input id="stampName" v-model="name" type="text" placeholder="이름을 입력해주세요" autocomplete="name" :disabled="smsSent" />
					</div>
					<div class="stamp-entry-field">
						<label for="stampPhone">연락처 입력</label>
						<input
							id="stampPhone"
							v-model="phone"
							type="tel"
							inputmode="numeric"
							maxlength="11"
							autocomplete="tel"
							placeholder="연락처를 입력해주세요 (숫자만)"
							:disabled="smsSent"
							@input="phone = phone.replace(/[^0-9]/g, '')"
						/>
					</div>

					<button v-if="!smsSent" type="submit" class="stamp-entry-primary" :disabled="loading">
						{{ loading ? '발송 중...' : '인증번호 받기' }}
					</button>

					<div v-else class="stamp-entry-verify">
						<div class="stamp-entry-field">
							<label for="stampCode">인증번호 입력</label>
							<div class="stamp-entry-verify-row">
								<input
									id="stampCode"
									ref="codeInput"
									v-model="authCode"
									type="tel"
									inputmode="numeric"
									maxlength="6"
									placeholder="인증번호 6자리"
									@input="authCode = authCode.replace(/[^0-9]/g, '')"
								/>
								<button type="button" :disabled="loading" @click="sendAuthCode">재전송</button>
							</div>
						</div>
						<button type="submit" class="stamp-entry-primary dark" :disabled="loading">
							{{ loading ? '확인 중...' : '인증 완료하고 시작하기' }}
						</button>
					</div>

					<div v-if="errorText" class="stamp-entry-error">{{ errorText }}</div>
				</form>

				<div class="stamp-entry-privacy">
					<span class="stamp-entry-shield" aria-hidden="true"></span>
					<span>입력하신 정보는 스탬프 투어 참여 확인 용도로만 사용되며, 행사 종료 후 안전하게 삭제됩니다.</span>
				</div>
			</section>
			<div class="stamp-entry-footer">동물과 사람이 함께 행복한 하루,<br />2026 서울동물행복페스타</div>
		</main>
	</div>
</template>

<script>
import heroImg from '@/assets/images/stamp/animal_festa_hero.jpg'
import logoImg from '@/assets/images/stamp/animal_festa_logo.png'
import { loadStampUser, saveStampUser } from './stampUser'

export default {
	metaInfo() {
		return { title: '모바일 스탬프 투어' }
	},
	data() {
		return {
			name: '',
			phone: '',
			authCode: '',
			sentCode: '',
			smsSent: false,
			loading: false,
			errorText: '',
			heroImg,
			logoImg,
		}
	},
	computed: {
		groupcode() {
			return this.$route.params.groupcode
		},
	},
	created() {
		// 이미 인증한 참여자(localStorage 저장)는 인증 없이 바로 메인으로 이동
		if (loadStampUser(this.groupcode)) {
			this.$router.replace({ name: 'stampMain', params: { groupcode: this.groupcode } })
			return
		}
		// 행사 안내(동물행복위크) 팝업은 인증 후 메인에서 노출 → 사업지 지정(?business=)을 메인으로 전달
		if (this.$route.query.business) sessionStorage.setItem('stamp-business-' + this.groupcode, this.$route.query.business)
	},
	methods: {
		// 인증번호 SMS 발송 (기존 createSendSm 모듈 재사용)
		async sendAuthCode() {
			this.errorText = ''
			if (!this.name.trim()) {
				this.errorText = '이름을 입력해주세요.'
				return
			}
			if (!/^01[016789][0-9]{7,8}$/.test(this.phone)) {
				this.errorText = '올바른 휴대폰 번호를 입력해주세요.'
				return
			}
			this.loading = true
			try {
				let randomCode = Math.floor(Math.random() * 1000000) + 100000
				if (randomCode > 1000000) randomCode = randomCode - 100000
				this.sentCode = String(randomCode)
				await this.$store.dispatch('createSendSm', {
					receiver: this.phone,
					message: `[스탬프 투어] 휴대전화 인증번호는 [${this.sentCode}] 입니다.`,
					authCode: this.sentCode,
				})
				this.smsSent = true
				this.authCode = ''
				this.$nextTick(() => this.$refs.codeInput && this.$refs.codeInput.focus())
			} catch (err) {
				console.log({ err })
				this.errorText = '인증번호 발송에 실패하였습니다. 잠시 후 다시 시도해주세요.'
			} finally {
				this.loading = false
			}
		},
		// 인증번호 확인 (기존 sendSms 조회 모듈 재사용)
		async verifyAuthCode() {
			this.errorText = ''
			if (!this.authCode) {
				this.errorText = '인증번호를 입력해주세요.'
				return
			}
			this.loading = true
			try {
				const res = await this.$store.dispatch('sendSms', {
					receiver: this.phone,
					authCode: String(this.authCode),
				})
				if (res.data.sendSms.length > 0) {
					saveStampUser(this.groupcode, { name: this.name.trim(), phone: this.phone })
					// 메인 진입 시 동물행복위크(행사 안내) 팝업 1회 노출
					sessionStorage.setItem('stamp-week-popup-' + this.groupcode, '1')
					this.$router.replace({ name: 'stampMain', params: { groupcode: this.groupcode } })
				} else {
					this.errorText = '인증번호가 틀렸습니다.'
				}
			} catch (err) {
				console.log({ err })
				this.errorText = '인증 확인 중 오류가 발생하였습니다.'
			} finally {
				this.loading = false
			}
		},
	},
}
</script>

<style>
@import url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/variable/pretendardvariable-dynamic-subset.min.css');
</style>

<style lang="scss" scoped>
$blue: #118fd8;
$yellow: #ffd733;
$ink: #1c252e;
$muted: #6e7883;

.stamp-entry-page {
	min-height: 100vh;
	display: flex;
	justify-content: center;
	align-items: flex-start; // 앱 높이가 컨텐츠만큼 늘어나도록 (stretch 시 하단 잘림)
	background: #eef3f7;
	font-family: 'Pretendard Variable', 'Pretendard', 'Apple SD Gothic Neo', 'Noto Sans KR', system-ui, -apple-system, sans-serif;
	color: $ink;
	* {
		box-sizing: border-box;
	}
	button,
	input {
		font-family: inherit;
	}
}
.stamp-entry-app {
	width: 100%;
	max-width: 430px;
	min-height: 100vh;
	background: $blue;
	position: relative;
	overflow: hidden;
}
.stamp-entry-hero {
	height: 236px;
	background: $yellow;
	position: relative;
	overflow: hidden;
	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}
	// 하단 물결(스캘럽) 장식
	&::after {
		content: '';
		position: absolute;
		left: 0;
		right: 0;
		bottom: -1px;
		height: 18px;
		background: radial-gradient(circle at 9px 0, transparent 9px, #fff 9.8px) 0 0/18px 18px repeat-x;
		transform: rotate(180deg);
	}
}
.stamp-entry-content {
	position: relative;
	background: #fff;
	margin: -1px 18px 26px;
	border-radius: 26px;
	padding: 26px 22px 22px;
	box-shadow: 0 18px 45px rgba(25, 63, 92, 0.14);
}
.stamp-entry-logo {
	display: flex;
	justify-content: center;
	margin: -4px auto 12px;
	img {
		display: block;
		width: 164px;
		height: auto;
		max-width: 100%;
	}
}
.stamp-entry-headline {
	text-align: center;
	margin-bottom: 21px;
	h1 {
		font-size: 24px;
		line-height: 1.25;
		margin: 0 0 8px;
		font-weight: 900;
		letter-spacing: -0.045em;
	}
	p {
		margin: 0;
		color: $muted;
		font-size: 14px;
		line-height: 1.55;
		letter-spacing: -0.025em;
	}
}
.stamp-entry-field {
	margin-top: 16px;
	label {
		display: block;
		font-size: 14px;
		font-weight: 800;
		margin-bottom: 8px;
		letter-spacing: -0.025em;
	}
	input {
		width: 100%;
		height: 52px;
		border: 1px solid #d7dee6;
		border-radius: 12px;
		padding: 0 15px;
		font-size: 15px;
		outline: none;
		background: #fff;
		color: $ink;
		transition: 0.15s;
		&:focus {
			border-color: $blue;
			box-shadow: 0 0 0 4px rgba(17, 143, 216, 0.11);
		}
		&::placeholder {
			color: #a5afb9;
		}
		&:disabled {
			background: #f5f7f9;
			color: $muted;
		}
	}
}
.stamp-entry-primary {
	margin-top: 20px;
	width: 100%;
	height: 56px;
	border: 0;
	border-radius: 14px;
	background: linear-gradient(180deg, #169dea 0%, #0b84ce 100%);
	color: #fff;
	font-size: 18px;
	font-weight: 900;
	letter-spacing: -0.025em;
	box-shadow: 0 10px 18px rgba(8, 125, 197, 0.25);
	cursor: pointer;
	&:active {
		transform: translateY(1px);
	}
	&:disabled {
		opacity: 0.7;
		cursor: default;
	}
	&.dark {
		background: $ink;
		box-shadow: none;
	}
}
.stamp-entry-verify {
	animation: stampEntryFadeUp 0.2s ease both;
}
.stamp-entry-verify-row {
	display: grid;
	grid-template-columns: 1fr 94px;
	gap: 8px;
	button {
		height: 52px;
		border: 1px solid #c9d3dd;
		border-radius: 12px;
		background: #f7f9fb;
		color: #4e5b67;
		font-weight: 800;
		font-size: 13px;
		cursor: pointer;
	}
}
.stamp-entry-error {
	margin-top: 12px;
	color: #ef4136;
	font-size: 13px;
	font-weight: 700;
	text-align: center;
}
.stamp-entry-privacy {
	display: flex;
	align-items: flex-start;
	gap: 8px;
	justify-content: center;
	margin: 18px auto 0;
	max-width: 310px;
	color: #89939d;
	font-size: 11.5px;
	line-height: 1.5;
	text-align: left;
}
.stamp-entry-shield {
	flex: 0 0 auto;
	width: 16px;
	height: 18px;
	margin-top: 1px;
	position: relative;
	&::before {
		content: '';
		position: absolute;
		inset: 1px 2px 3px;
		border: 1.8px solid #8aa0b3;
		border-radius: 6px 6px 8px 8px;
		clip-path: polygon(50% 0, 100% 18%, 90% 76%, 50% 100%, 10% 76%, 0 18%);
	}
}
.stamp-entry-footer {
	color: #eaf7ff;
	text-align: center;
	font-size: 11px;
	line-height: 1.5;
	padding: 0 24px 24px;
	opacity: 0.95;
}
@keyframes stampEntryFadeUp {
	from {
		opacity: 0;
		transform: translateY(5px);
	}
	to {
		opacity: 1;
		transform: none;
	}
}
@media (max-width: 360px) {
	.stamp-entry-hero {
		height: 218px;
	}
	.stamp-entry-content {
		margin-left: 12px;
		margin-right: 12px;
		padding-left: 18px;
		padding-right: 18px;
	}
	.stamp-entry-headline h1 {
		font-size: 22px;
	}
}
@media (min-width: 431px) {
	.stamp-entry-app {
		margin: 18px 0;
		border-radius: 26px;
		min-height: calc(100vh - 36px);
		box-shadow: 0 20px 60px rgba(18, 40, 60, 0.14);
	}
}
</style>
